import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Send auth token with every request when present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

export const setAuthToken = (token: string) => {
  api.defaults.headers.common['x-auth-token'] = token;
};

export interface SystemStatus {
  status: string;
  killSwitchEnabled: boolean;
  schedulerRunning: boolean;
  currentPosition: {
    symbol: string;
    quantity: number;
    entryPrice: number;
    entryDate: string;
    stopLossPercent?: number;
  } | null;
  accountBalance: number;
  lastTradeDate: string | null;
  lastUpdate: string;
  simulationMode: boolean;
  balanceAtMarketOpen?: number;
  balanceAtMarketClose?: number;
  etfHeldOvernight?: string;
  etfBoughtAtMarketOpen?: string | null;
  stopLossPercentAtMarketOpen?: number | null;
  marketOpenTime?: string;
  marketCloseTime?: string;
  /** Schwab token is valid (not expired/invalid). When false, show reconnect alert on Dashboard. */
  tokenValid?: boolean;
}

export interface TradeExecution {
  orderId: string;
  symbol: string;
  action: string;
  quantity: number;
  price: number;
  timestamp: string;
  status: 'PENDING' | 'FILLED' | 'REJECTED' | 'CANCELLED';
  error?: string;
}

/** Trading log entry (market open/close event from backend JSON file). */
export interface TradingLogEntry {
  id: string;
  timestamp: string;
  type: 'MARKET_OPEN' | 'MARKET_CLOSE';
  decision?: string;
  reason?: string;
  executions: Array<{
    orderId: string;
    symbol: string;
    action: string;
    quantity: number;
    price: number;
    timestamp: string;
    status: string;
    error?: string;
  }>;
  skipped?: boolean;
  skipReason?: string;
}

export const apiService = {
  setAuthToken,

  // Get system status
  getStatus: async (): Promise<SystemStatus> => {
    const response = await api.get('/status');
    return response.data;
  },

  // Get trade history
  getTradeHistory: async (limit?: number): Promise<{ history: TradeExecution[] }> => {
    const params = limit ? { limit } : {};
    const response = await api.get('/status/history', { params });
    return response.data;
  },

  // Get kill switch status
  getKillSwitch: async (): Promise<{ enabled: boolean }> => {
    const response = await api.get('/trading/kill-switch');
    return response.data;
  },

  // Toggle kill switch
  setKillSwitch: async (enabled: boolean): Promise<{ success: boolean; killSwitchEnabled: boolean }> => {
    const response = await api.post('/trading/kill-switch', { enabled });
    return response.data;
  },

  // Get current position
  getPosition: async (): Promise<{ position: { symbol: string; quantity: number } | null }> => {
    const response = await api.get('/trading/position');
    return response.data;
  },

  // Get account balance
  getBalance: async (): Promise<{ balance: number }> => {
    const response = await api.get('/trading/balance');
    return response.data;
  },

  // Manually trigger market open trade
  triggerMarketOpen: async (): Promise<{ success: boolean; executions: TradeExecution[] }> => {
    const response = await api.post('/trading/trigger/open');
    return response.data;
  },

  // Manually trigger market close trade
  triggerMarketClose: async (): Promise<{ success: boolean; executions: TradeExecution[] }> => {
    const response = await api.post('/trading/trigger/close');
    return response.data;
  },

  // Get trading logs (market open/close events)
  getLogs: async (limit?: number): Promise<{ logs: TradingLogEntry[]; logFilePath?: string }> => {
    const params = limit ? { limit } : {};
    const response = await api.get('/status/logs', { params });
    return response.data;
  },
};

export default api;
